import {useAtomValue, useStore} from 'jotai';
import type React from 'react';
import {useRef, useCallback} from 'react';
import {useGetActiveScreenId} from '@features/activeScreenIdContext/useGetActiveScreenId';
import amplitudeService from '@features/analytics/amplitudeService';
import {trpc} from '@features/utils/trpc';
import {frameSelectionAtom, modeAtom, hoveredElementAtom} from './atoms';
import {useFrameSelectionActions} from './useFrameSelection';
import {isTextElement} from './isTextElement';
import {isComponentScreen} from '@features/flow/utils/componentScreenId';
import {useComponentUpdateText} from './useComponentUpdateText';
import {setSyncStatus} from './syncStatusAtom';
import {assertExist} from '@features/utils/assertExist';
import {isEditableElementFocused} from '@features/utils/dom';
import {useSelectedScreenIds} from '@features/activeScreenIdContext/useSelectedScreenIds';

interface UsePartialEditProps {
  chatId: string;
  flowId: string;
}

/**
 * Cleans up all partial-edit selection artifacts from the DOM.
 * The only persistent DOM artifact we leave behind is `contenteditable`, and
 * it only lives inside frame shadow roots, so we scope the sweep to them.
 */
export function cleanupPartialEditSelection(): void {
  document.querySelectorAll('[data-banani-screenid]').forEach((host) => {
    host.shadowRoot?.querySelectorAll('[contenteditable]').forEach((el) => {
      el.removeAttribute('contenteditable');
    });
  });
}

function cleanupPartialEditSelectionForElement(
  element: HTMLElement | null,
): void {
  const root = element?.getRootNode();
  if (root instanceof ShadowRoot) {
    root.querySelectorAll('[contenteditable]').forEach((el) => {
      el.removeAttribute('contenteditable');
    });
    return;
  }

  cleanupPartialEditSelection();
}

/** Gets the frame content container from an element inside the shadow DOM. */
function getFrameContainer(element: HTMLElement): HTMLElement | null {
  const root = element.getRootNode();
  if (root instanceof ShadowRoot) {
    const container = root.firstElementChild;
    if (container instanceof HTMLElement) return container;
  }
  return null;
}

/** Removes all editor artifacts from a container to prevent false positives in HTML comparison. */
function removeEditorArtifacts(container: HTMLElement): void {
  container.removeAttribute('contenteditable');
  container.querySelectorAll('[contenteditable]').forEach((el) => {
    el.removeAttribute('contenteditable');
  });
}

/** Clears editing state from an element. */
function clearElementEditing(element: HTMLElement | null): void {
  if (!element) return;
  element.removeAttribute('contenteditable');
}

/** Hook for partial-edit mode: element selection and inline text editing. */
export const usePartialEdit = ({chatId, flowId}: UsePartialEditProps) => {
  const mode = useAtomValue(modeAtom);
  const isInspectorMode = mode === 'partial-edit';
  const store = useStore();
  const getActiveScreenId = useGetActiveScreenId();
  const selectedScreenIds = useSelectedScreenIds();
  const {getFrameSelection, setActiveSelection, clearFrameSelection} =
    useFrameSelectionActions();

  // Refs that survive across renders. The *selection* itself lives in
  // `frameSelectionAtom`; we only keep refs for state that isn't on the
  // atom (original HTML for cancel-on-Escape, listener teardown).
  const originalHtmlRef = useRef<string | null>(null);
  const cleanupListenersRef = useRef<(() => void) | null>(null);

  const trpcUtils = trpc.useUtils();
  const saveEditedStructureMutation =
    trpc.screens.saveEditedStructure.useMutation({
      onSuccess: async ({flowId: updatedFlowId, screenId}) => {
        await trpcUtils.screens.getActiveStructure.invalidate({screenId});
        await trpcUtils.screens.getStructureList.invalidate({screenId});
        if (updatedFlowId) {
          await trpcUtils.flow.getFlow.invalidate({flowId: updatedFlowId});
        }
      },
    });

  const {updateComponentTextAsync} = useComponentUpdateText();

  // Cleanup event listeners
  const cleanupListeners = useCallback(() => {
    if (cleanupListenersRef.current) {
      cleanupListenersRef.current();
      cleanupListenersRef.current = null;
    }
  }, []);

  // Save the edited HTML if changed. Reads the currently-editing element
  // directly off the state atom so there's no separate ref to keep in sync.
  const saveEditedHtml = useCallback(async () => {
    const curr = getFrameSelection();
    if (curr.kind !== 'editing-text') return;

    const element = curr.element;
    const container = getFrameContainer(element);
    if (!container) return;

    // Remove styling before getting HTML to avoid saving editor artifacts
    clearElementEditing(element);
    // Remove all editor artifacts to prevent false positives in comparison
    removeEditorArtifacts(container);

    const newHtml = container.innerHTML;

    // Only save if HTML actually changed
    if (newHtml !== originalHtmlRef.current) {
      setSyncStatus('syncing');
      try {
        if (isComponentScreen(chatId)) {
          const file = element.getAttribute('data-file');
          assertExist(file, 'must have file attribte');
          const idx = Number(element.getAttribute('data-idx'));

          await updateComponentTextAsync({
            location: {file, idx},
            newOutputHtml: newHtml,
            newText: element.textContent,
            screenId: chatId,
          });
          amplitudeService.logEvent(
            '[Flow] Inline text edit saved (components)',
            {
              flowId,
              chatId,
            },
          );
        } else {
          await saveEditedStructureMutation.mutateAsync({
            screenId: chatId,
            htmlCss: newHtml,
          });
          setSyncStatus('success');
          amplitudeService.logEvent('[Flow] Inline text edit saved', {
            flowId,
            chatId,
          });
        }
      } catch (error) {
        setSyncStatus('idle');
        console.error('Failed to save edited structure:', error);
      }
    }
  }, [
    chatId,
    flowId,
    getFrameSelection,
    saveEditedStructureMutation,
    updateComponentTextAsync,
  ]);

  // Tear down the current inner selection. Mode (`partial-edit` ↔ `generate`)
  // is owned by useSelectModeShortcut — it flips back automatically when the
  // active screen still has content, so we don't touch it here.
  const clearInnerSelection = useCallback(() => {
    cleanupListeners();
    const curr = getFrameSelection();
    const currentSelectedElement = curr.kind === 'none' ? null : curr.element;
    clearElementEditing(currentSelectedElement);
    cleanupPartialEditSelectionForElement(currentSelectedElement);
    originalHtmlRef.current = null;
    clearFrameSelection();
  }, [cleanupListeners, getFrameSelection, clearFrameSelection]);

  const saveAndClear = useCallback(async () => {
    await saveEditedHtml();
    clearInnerSelection();
  }, [saveEditedHtml, clearInnerSelection]);

  // Shared: swap selection from the current element to `target`, sync the
  // atom, and optionally snap the hover overlay. Always lands in
  // `kind: 'element'` — entering text-editing is a separate transition.
  const selectElement = useCallback(
    (target: HTMLElement, {syncHover}: {syncHover?: boolean} = {}) => {
      const curr = getFrameSelection();
      const prev = curr.kind === 'none' ? null : curr.element;
      if (prev && prev !== target) clearElementEditing(prev);
      setActiveSelection({kind: 'element', screenId: chatId, element: target});
      if (syncHover) {
        store.set(hoveredElementAtom, {
          screenId: chatId,
          elementReference: target,
        });
      }
    },
    [chatId, getFrameSelection, setActiveSelection, store],
  );

  // Move the current selection to its parent element (Shift+Enter shortcut).
  // When the parent would be the screen container itself, clear the inner
  // selection so the screen becomes the active selection again.
  const moveSelectionToParent = useCallback(() => {
    const curr = getFrameSelection();
    if (curr.kind !== 'element') return; // ignore while editing text or idle
    const current = curr.element;
    const parent = current.parentElement;
    if (!parent || parent.hasAttribute('data-banani-screenid')) {
      clearInnerSelection();
      return;
    }
    selectElement(parent);
    // If the cursor was hovering inside the new parent, snap the hover overlay
    // to it so the parent shows both selected and hovered states immediately,
    // without needing a mouse move to refire.
    const currentHover = store.get(hoveredElementAtom);
    if (
      currentHover?.screenId === chatId &&
      parent.contains(currentHover.elementReference)
    ) {
      store.set(hoveredElementAtom, {
        screenId: chatId,
        elementReference: parent,
      });
    }
  }, [chatId, clearInnerSelection, getFrameSelection, selectElement, store]);

  // Select the first visible child HTMLElement inside the current selection.
  const moveSelectionToFirstChild = useCallback(() => {
    const curr = getFrameSelection();
    if (curr.kind !== 'element') return;
    const child = Array.from(curr.element.children).find(
      (el): el is HTMLElement =>
        el instanceof HTMLElement && el.offsetWidth > 0 && el.offsetHeight > 0,
    );
    if (!child) return;
    selectElement(child, {syncHover: true});
  }, [getFrameSelection, selectElement]);

  // Move the selection to the next/previous sibling within the same parent
  // (Tab / Shift+Tab). Wraps around at the ends. Skips invisible siblings.
  const moveSelectionToSibling = useCallback(
    (direction: 'next' | 'previous') => {
      const curr = getFrameSelection();
      if (curr.kind !== 'element') return;
      const current = curr.element;
      const parent = current.parentElement;
      if (!parent) return;
      const siblings = Array.from(parent.children).filter(
        (child): child is HTMLElement =>
          child instanceof HTMLElement &&
          child.offsetWidth > 0 &&
          child.offsetHeight > 0,
      );
      if (siblings.length < 2) return;
      const index = siblings.indexOf(current);
      if (index === -1) return;
      const nextIndex =
        direction === 'next'
          ? (index + 1) % siblings.length
          : (index - 1 + siblings.length) % siblings.length;
      selectElement(siblings[nextIndex], {syncHover: true});
    },
    [getFrameSelection, selectElement],
  );

  // Wire up keyboard navigation while an element is selected:
  //   Escape        — clear selection
  //   Enter         — walk down to first child
  //   Shift+Enter   — walk up to parent
  //   Tab / Shift+Tab — cycle siblings (wraps, skips invisible)
  const setupSelectionKeyListener = useCallback(() => {
    // Tear down any previous listener so multiple selections don't stack
    // handlers on the same key press.
    cleanupListenersRef.current?.();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableElementFocused()) return;
      if (event.key === 'Escape') {
        clearInnerSelection();
        return;
      }
      if (event.key === 'Enter' && event.shiftKey) {
        event.preventDefault();
        moveSelectionToParent();
        return;
      }
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        moveSelectionToFirstChild();
        return;
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        moveSelectionToSibling(event.shiftKey ? 'previous' : 'next');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Auto-remove listener when the selection is cleared externally
    // (e.g. switching screens via onNodeClick).
    const unsubAtom = store.sub(frameSelectionAtom, () => {
      if (getFrameSelection().kind === 'none') {
        cleanupListenersRef.current?.();
      }
    });

    // Store cleanup function
    cleanupListenersRef.current = () => {
      document.removeEventListener('keydown', handleKeyDown);
      unsubAtom();
    };
  }, [
    store,
    clearInnerSelection,
    getFrameSelection,
    moveSelectionToParent,
    moveSelectionToSibling,
    moveSelectionToFirstChild,
  ]);

  // Setup event listeners for text editing (keyboard and click outside)
  const setupTextEditingListeners = useCallback(
    (element: HTMLElement) => {
      // Sanitize pasted content to plain text only
      const handlePaste = (event: ClipboardEvent) => {
        event.preventDefault();
        const text = event.clipboardData?.getData('text/plain') ?? '';

        // Get selection from the Shadow DOM root, not the main document
        // window.getSelection() doesn't work for selections inside Shadow DOM
        // ShadowRoot.getSelection() is non-standard but supported in Chrome/Safari
        const root = element.getRootNode();
        const shadowRootWithSelection = root as ShadowRoot & {
          getSelection?: () => Selection | null;
        };
        const selection =
          root instanceof ShadowRoot && shadowRootWithSelection.getSelection
            ? shadowRootWithSelection.getSelection()
            : window.getSelection();

        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          range.insertNode(document.createTextNode(text));
          range.collapse(false);
        }
      };

      const handleKeyDown = (event: KeyboardEvent) => {
        // Save on Shift+Enter
        if (event.key === 'Enter' && event.shiftKey) {
          event.preventDefault();
          void saveAndClear();
        }
        // Cancel on Escape
        if (event.key === 'Escape') {
          // Restore original HTML
          const container = getFrameContainer(element);
          if (container && originalHtmlRef.current) {
            container.innerHTML = originalHtmlRef.current;
          }
          clearInnerSelection();
        }
      };

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target;
        if (!(target instanceof Node)) return;

        const elementRoot = element.getRootNode();

        // Check if click is inside the same shadow root as the editing element
        // Events from shadow DOM bubble with the shadow host as target, so we need to check:
        // 1. If target is in the same shadow root (direct check)
        // 2. If target is the shadow host that contains our element's shadow root
        const targetRoot = target.getRootNode();
        const isSameShadowRoot = elementRoot === targetRoot;

        const isShadowHost =
          target instanceof HTMLElement &&
          elementRoot instanceof ShadowRoot &&
          elementRoot.host === target;

        if (isSameShadowRoot || isShadowHost) return;

        void saveAndClear();
      };

      // Prevent click events on the element from propagating
      // This stops keyboard-triggered clicks (Space/Enter on buttons) from triggering save
      const handleClickOnElement = (event: MouseEvent) => {
        event.stopPropagation();
      };

      // Add listeners
      element.addEventListener('keydown', handleKeyDown);
      element.addEventListener('paste', handlePaste);
      element.addEventListener('click', handleClickOnElement, true);
      document.addEventListener('click', handleClickOutside, true);

      // Store cleanup function
      cleanupListenersRef.current = () => {
        element.removeEventListener('keydown', handleKeyDown);
        element.removeEventListener('paste', handlePaste);
        element.removeEventListener('click', handleClickOnElement, true);
        document.removeEventListener('click', handleClickOutside, true);
      };
    },
    [saveAndClear, clearInnerSelection],
  );

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      // Let banani-button clicks on non-active screens propagate so
      // useBananiButtonClick can trigger generate-next.
      const targetScreenId = target
        .closest('[data-banani-screenid]')
        ?.getAttribute('data-banani-screenid');
      if (
        targetScreenId &&
        targetScreenId !== getActiveScreenId() &&
        target.closest('[data-media-type="banani-button"]')
      ) {
        return;
      }

      // Clicking on a different screen — let the event propagate so
      // onNodeClick handles proper screen switching and selection clearing.
      if (targetScreenId && targetScreenId !== getActiveScreenId()) {
        return;
      }

      // Same screen — handle selection, stop propagation to prevent
      // onNodeClick from clearing what we just selected.
      event.preventDefault();
      event.stopPropagation();

      const curr = getFrameSelection();

      // If clicking on the same element that's being edited, don't do anything
      if (curr.kind === 'editing-text' && curr.element === target) {
        return;
      }

      // If we're editing a different element, save it first. saveEditedHtml
      // reads the current editing target off the atom; selectElement below
      // will transition us back to `kind: 'element'`.
      if (curr.kind === 'editing-text') {
        void saveEditedHtml();
        cleanupListeners();
        originalHtmlRef.current = null;
      }

      selectElement(target);
      setupSelectionKeyListener();

      amplitudeService.logEvent('[Flow] Element selected for editing', {
        flowId,
        chatId,
      });
    },
    [
      chatId,
      flowId,
      getActiveScreenId,
      getFrameSelection,
      saveEditedHtml,
      cleanupListeners,
      setupSelectionKeyListener,
      selectElement,
    ],
  );

  // Double-click to enter contentEditable mode for text elements
  const handleDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const target = event.target;
      if (!(target instanceof HTMLElement)) return;

      if (isComponentScreen(chatId)) {
        if (
          !target.hasAttribute('data-file') ||
          !target.hasAttribute('data-idx')
        ) {
          return;
        }
      } else {
        if (!isTextElement(target)) return;
      }

      const curr = getFrameSelection();

      // If already editing this element, do nothing
      if (curr.kind === 'editing-text' && curr.element === target) {
        return;
      }

      // If editing a different element, save it first
      if (curr.kind === 'editing-text') {
        void saveEditedHtml();
        cleanupListeners();
        originalHtmlRef.current = null;
      }

      // Store original HTML for potential cancel (before adding selection class)
      const container = getFrameContainer(target);
      if (container) {
        removeEditorArtifacts(container);
        originalHtmlRef.current = container.innerHTML;
      }

      // Double-click arrives after click has already installed the selection
      // key listener. Tear it down before text-edit listeners take over so
      // Tab keeps its native contentEditable behavior.
      cleanupListeners();

      // Make element editable and place cursor at the end
      target.contentEditable = 'true';
      target.focus();
      const range = document.createRange();
      range.selectNodeContents(target);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);

      // Transition directly to editing-text — one atom write captures
      // selection + edit mode atomically.
      setActiveSelection({
        kind: 'editing-text',
        screenId: chatId,
        element: target,
      });

      // Setup keyboard and click outside listeners
      setupTextEditingListeners(target);

      amplitudeService.logEvent('[Flow] Text element double-clicked for edit', {
        flowId,
        chatId,
      });
    },
    [
      chatId,
      flowId,
      getFrameSelection,
      saveEditedHtml,
      cleanupListeners,
      setActiveSelection,
      setupTextEditingListeners,
    ],
  );

  const partialEditEnabled = isInspectorMode && selectedScreenIds.length <= 1;
  return {
    partialEditEnabled,
    handleClick: partialEditEnabled ? handleClick : undefined,
    handleDoubleClick: partialEditEnabled ? handleDoubleClick : undefined,
  };
};
